import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useState } from 'react';
import { JEV_CORRECTION_LABELS, JEV_EVIDENCE_LABELS, type CorrectJevRequest, type JevAskResults, type JevAskView, type JevCorrectionView, type JevEvidence, type JevPersonFindings, type JevPersonResult, type JevStatus, type RunJevAskInput } from '@nest-recruit/shared';
import { apiGet, apiPost, errorMessage, type ApiError } from '../../../lib/api-client';
import { useCurrentSession } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';

// ADR 0050/0051 — Jev on RNK-01. Under `['applications', 'jev', jobId]`, the key the server's progress hints name, so a
// running ask is followed with no polling (#12).

export function jevKey(jobId: string): readonly unknown[] {
  return ['applications', 'jev', jobId];
}

const STATUS_KEY = ['jev', 'status'] as const;
const STATUS_STALE_MS = 5 * 60_000;

/** Who would answer an ask now: Jev, the practice stand-in, or nobody. */
export function useJevStatus(isOn: boolean): UseQueryResult<JevStatus, ApiError> {
  return useQuery<JevStatus, ApiError>({ queryKey: STATUS_KEY, queryFn: () => apiGet<JevStatus>('/jev/status'), enabled: isOn, staleTime: STATUS_STALE_MS });
}

/** The job's asks, newest first — the panel's "Recent asks". */
export function useJevAsks(jobId: string, isOn: boolean): UseQueryResult<readonly JevAskView[], ApiError> {
  return useQuery<readonly JevAskView[], ApiError>({
    queryKey: [...jevKey(jobId), 'asks'],
    queryFn: () => apiGet<readonly JevAskView[]>(`/jobs/${jobId}/ranking/jev/asks`),
    enabled: isOn,
    placeholderData: (previous) => previous,
  });
}

/** One ask and what everyone got. The previous ask stays on screen while the next one loads (CLAUDE.md §5a rule 2). */
export function useJevAsk(jobId: string, askId: string | undefined): UseQueryResult<JevAskResults, ApiError> {
  return useQuery<JevAskResults, ApiError>({
    queryKey: [...jevKey(jobId), 'ask', askId ?? ''],
    queryFn: () => apiGet<JevAskResults>(`/jobs/${jobId}/ranking/jev/asks/${askId ?? ''}`),
    enabled: askId !== undefined,
    placeholderData: (previous) => previous,
  });
}

/**
 * ADR 0062: one person's findings for the ask that read them, for the sheet. The ranking no longer downloads a whole ask to order
 * the list (it ranks from each row's stored reading), so the sheet asks for just the one person.
 */
export function useJevPerson(jobId: string, askId: string | undefined, applicationId: string | undefined): UseQueryResult<JevPersonFindings, ApiError> {
  return useQuery<JevPersonFindings, ApiError>({
    queryKey: personKey(jobId, askId ?? '', applicationId ?? ''),
    queryFn: () => apiGet<JevPersonFindings>(`/jobs/${jobId}/ranking/jev/asks/${askId ?? ''}/people/${applicationId ?? ''}`),
    enabled: askId !== undefined && applicationId !== undefined,
    placeholderData: (previous) => (previous?.applicationId === applicationId ? previous : undefined),
  });
}

function personKey(jobId: string, askId: string, applicationId: string): readonly unknown[] {
  return [...jevKey(jobId), 'person', askId, applicationId];
}

/**
 * F23.17 (ADR 0051 §6): Haiku's reason for one person, written on the server if it isn't yet — for someone below the
 * top 50 whose sheet is opened. The answer is put into the ask's results too, so the list shows it as well.
 */
export function useJevReason(jobId: string, askId: string | undefined, applicationId: string | undefined, isNeeded: boolean): UseQueryResult<JevPersonResult, ApiError> {
  const queryClient = useQueryClient();
  return useQuery<JevPersonResult, ApiError>({
    queryKey: [...jevKey(jobId), 'reason', askId ?? '', applicationId ?? ''],
    queryFn: async () => {
      const person = await apiPost<JevPersonResult>(`/jobs/${jobId}/ranking/jev/asks/${askId ?? ''}/people/${applicationId ?? ''}/reason`, {});
      queryClient.setQueryData<JevAskResults>([...jevKey(jobId), 'ask', askId ?? ''], (results) =>
        results ? { ...results, people: results.people.map((entry) => (entry.applicationId === person.applicationId ? person : entry)) } : results,
      );
      // The sheet's copy also carries the ask's lines (`JevPersonFindings`): only what the reason adds is laid over it.
      queryClient.setQueryData<JevPersonFindings>(personKey(jobId, askId ?? '', person.applicationId), (previous) => (previous ? { ...previous, ...person } : previous));
      return person;
    },
    enabled: isNeeded && askId !== undefined && applicationId !== undefined,
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * F23.15: start an ask. The panel shows "Jev is reading…" from the click (#11); the address takes the new ask's id
 * the moment the server has it. A refusal (an unfair ask, Jev not connected) says why and changes nothing.
 */
export function useRunJevAsk(jobId: string, onStarted: (ask: JevAskView) => void): { readonly run: (input: RunJevAskInput) => void; readonly isStarting: boolean; readonly lastInput: RunJevAskInput | null } {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const [lastInput, setLastInput] = useState<RunJevAskInput | null>(null);
  const mutation = useMutation<JevAskView, ApiError, RunJevAskInput>({
    mutationFn: (input) => apiPost<JevAskView>(`/jobs/${jobId}/ranking/jev/asks`, input),
    onMutate: (input) => setLastInput(input),
    onSuccess: (ask) => {
      queryClient.setQueryData<JevAskResults>([...jevKey(jobId), 'ask', ask.id], { ask, people: [] });
      onStarted(ask);
      void queryClient.invalidateQueries({ queryKey: [...jevKey(jobId), 'asks'] });
    },
    onError: (error) => show({ tone: 'danger', message: 'Jev didn’t run', detail: errorMessage(error) }),
  });
  return { run: (input) => mutation.mutate(input), isStarting: mutation.isPending, lastInput };
}

// ---- F23.19 (ADR 0059): recruiters correct Jev's findings ----

const JEV_GRADES: readonly JevEvidence[] = ['none', 'named', 'related', 'shown', 'strong'];
let pendingCorrections = 0;

/** "The grade is wrong → Shown in real work": the reason, and the grade the recruiter says is right. */
export function correctionLine(correction: Pick<JevCorrectionView, 'code' | 'correctedLevel'>): string {
  const grade = correction.correctedLevel === null ? undefined : JEV_GRADES[correction.correctedLevel];
  return grade ? `${JEV_CORRECTION_LABELS[correction.code]} → ${JEV_EVIDENCE_LABELS[grade]}` : JEV_CORRECTION_LABELS[correction.code];
}

/**
 * F23.19 with the instant feel (CLAUDE.md §5a): the correction shows at once, the server confirms, and the optimistic
 * row is replaced by the server's list only when it answers. A refusal puts it back and says why. Each one offers Undo
 * for 10 s, which withdraws it (the row stays, marked withdrawn). A correction moves nobody (Q6).
 */
export function useCorrectJev(jobId: string, askId: string): { readonly correct: (applicationId: string, input: CorrectJevRequest, subject: string) => Promise<void> } {
  const queryClient = useQueryClient();
  const userName = useCurrentSession()?.fullName ?? 'You';
  const show = useToasts((toasts) => toasts.show);
  const showUndo = useToasts((toasts) => toasts.showUndo);
  const askKey = [...jevKey(jobId), 'ask', askId];
  const base = `/jobs/${jobId}/ranking/jev/asks/${askId}`;

  // The sheet reads one person (ADR 0062) and the Ask Jev list reads the whole ask: a correction is laid over whichever is on screen.
  const onePersonKey = (applicationId: string): readonly unknown[] => personKey(jobId, askId, applicationId);
  const corrections = (applicationId: string): readonly JevCorrectionView[] =>
    queryClient.getQueryData<JevPersonFindings>(onePersonKey(applicationId))?.corrections ??
    queryClient.getQueryData<JevAskResults>(askKey)?.people.find((person) => person.applicationId === applicationId)?.corrections ??
    [];
  const setCorrections = (applicationId: string, next: readonly JevCorrectionView[]): void => {
    queryClient.setQueryData<JevAskResults>(askKey, (results) => (results ? { ...results, people: results.people.map((person) => (person.applicationId === applicationId ? { ...person, corrections: next } : person)) } : results));
    queryClient.setQueryData<JevPersonFindings>(onePersonKey(applicationId), (person) => (person ? { ...person, corrections: next } : person));
  };

  const withdraw = async (applicationId: string, correctionId: string): Promise<void> => {
    await Promise.all([queryClient.cancelQueries({ queryKey: askKey }), queryClient.cancelQueries({ queryKey: onePersonKey(applicationId) })]);
    const before = corrections(applicationId);
    setCorrections(applicationId, before.filter((entry) => entry.id !== correctionId));
    try {
      setCorrections(applicationId, await apiPost<readonly JevCorrectionView[]>(`${base}/corrections/${correctionId}/withdraw`, {}));
      await Promise.all([queryClient.invalidateQueries({ queryKey: askKey }), queryClient.invalidateQueries({ queryKey: onePersonKey(applicationId) })]);
    } catch (error) {
      setCorrections(applicationId, before);
      show({ tone: 'danger', message: 'The correction stayed', detail: errorMessage(error) });
    }
  };

  const correct = async (applicationId: string, input: CorrectJevRequest, subject: string): Promise<void> => {
    await Promise.all([queryClient.cancelQueries({ queryKey: askKey }), queryClient.cancelQueries({ queryKey: onePersonKey(applicationId) })]);
    const before = corrections(applicationId);
    pendingCorrections += 1;
    const pending: JevCorrectionView = { id: `pending-${pendingCorrections}`, partIndex: input.partIndex, code: input.code, correctedLevel: input.correctedLevel ?? null, note: input.note ?? null, byName: userName, at: new Date().toISOString() };
    setCorrections(applicationId, [...before, pending]);
    try {
      const saved = await apiPost<readonly JevCorrectionView[]>(`${base}/people/${applicationId}/corrections`, input);
      setCorrections(applicationId, saved);
      await Promise.all([queryClient.invalidateQueries({ queryKey: askKey }), queryClient.invalidateQueries({ queryKey: onePersonKey(applicationId) })]);
      const known = new Set(before.map((entry) => entry.id));
      const made = saved.filter((entry) => !known.has(entry.id)).at(-1);
      if (made) showUndo(`${subject}: ${correctionLine(made)}`, () => void withdraw(applicationId, made.id));
    } catch (error) {
      setCorrections(applicationId, before);
      show({ tone: 'danger', message: 'That wasn’t saved', detail: errorMessage(error) });
    }
  };

  return { correct };
}
